import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Sparkles,
  PoundSterling,
  Home,
  Heart,
  Star,
  CalendarCheck,
  Send,
  Copy,
  ClipboardCheck,
  FileText,
  MessageSquareQuote,
} from "lucide-react";
import { toast } from "sonner";
import "./ValuationFollowUp.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CTA_PRESETS = [
  { id: "list-this-week", label: "Let's get it on the market this week", icon: CalendarCheck },
  { id: "discuss-strategy", label: "Happy to discuss the marketing strategy further", icon: MessageSquareQuote },
  { id: "take-your-time", label: "Take your time — here when you're ready", icon: Heart },
  { id: "follow-up-call", label: "Lock in a follow-up call", icon: Send },
];

const FEATURE_SUGGESTIONS = [
  "the magnificent garden",
  "the south-facing terrace",
  "the kitchen extension",
  "the period features",
  "the open-plan living space",
  "the location on a quiet road",
  "the stunning views",
  "the master suite",
];

const PERSONAL_SUGGESTIONS = [
  "your dog was a cheeky chap",
  "loved chatting about the renovation",
  "your tea was the best I've had this week",
  "great to meet the whole family",
];

export default function ValuationFollowUpBuilder({ agentName, agencyName, agencyStyle }) {
  const [sellerName, setSellerName] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [proposedPrice, setProposedPrice] = useState("");
  const [scriptLength, setScriptLength] = useState("Short");
  const [specialFeature, setSpecialFeature] = useState("");
  const [personalTouch, setPersonalTouch] = useState("");
  const [comparableSales, setComparableSales] = useState("");
  const [selectedCta, setSelectedCta] = useState(CTA_PRESETS[0].label);
  const [generating, setGenerating] = useState(false);
  const [generatedScript, setGeneratedScript] = useState("");
  const [scriptId, setScriptId] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const [resolvedAgencyStyle, setResolvedAgencyStyle] = useState(agencyStyle || "Friendly & Conversational");
  useEffect(() => {
    if (agencyStyle) { setResolvedAgencyStyle(agencyStyle); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/my-script-settings`);
        if (r.data?.agency_style) setResolvedAgencyStyle(r.data.agency_style);
      } catch (_) { /* default */ }
    })();
  }, [agencyStyle]);

  const handleGenerate = async () => {
    if (!sellerName.trim()) {
      toast.error("Please enter the seller name(s)");
      return;
    }
    if (!proposedPrice.trim()) {
      toast.error("Please enter the proposed listing price");
      return;
    }
    setGenerating(true);
    setGeneratedScript("");
    try {
      const res = await axios.post(`${API}/valuation-followup/generate`, {
        seller_name: sellerName,
        property_address: propertyAddress,
        proposed_price: proposedPrice,
        script_length: scriptLength,
        special_feature: specialFeature,
        personal_touch: personalTouch,
        comparable_sales: comparableSales,
        cta: selectedCta,
        agent_name: agentName || "",
        agency_name: agencyName || "",
        agency_style: resolvedAgencyStyle,
      });
      setGeneratedScript(res.data.script);
      setScriptId(res.data.id);
      toast.success("Valuation follow-up script generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedScript);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1800);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders/submit`, {
        property_title: propertyAddress || sellerName || "Valuation Follow-Up",
        town: propertyAddress.split(",")[1]?.trim() || "",
        script: generatedScript,
        sections: [],
        order_type: "valuation_followup",
        script_length: scriptLength,
        tone: resolvedAgencyStyle,
        settings: {
          seller_name: sellerName,
          property_address: propertyAddress,
          proposed_price: proposedPrice,
          special_feature: specialFeature,
          personal_touch: personalTouch,
          cta: selectedCta,
          source_id: scriptId,
        },
      });
      toast.success("Sent to editors!");
      setSubmitOpen(false);
      setSellerName(""); setPropertyAddress(""); setProposedPrice("");
      setSpecialFeature(""); setPersonalTouch(""); setComparableSales("");
      setGeneratedScript(""); setScriptId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="vf-builder" data-testid="valuation-followup-builder">
      <div className="vf-layout">
        {/* Form Column */}
        <div className="vf-form-col">
          <div className="vf-form-header">
            <PoundSterling size={20} />
            <div>
              <h2>Valuation Follow-Up</h2>
              <p>A short covering note or fuller follow-up after a market appraisal.</p>
            </div>
          </div>

          <div className="vf-row-2">
            <div className="vf-field">
              <Label className="vf-label">Seller name(s)</Label>
              <Input
                value={sellerName}
                onChange={(e) => setSellerName(e.target.value)}
                placeholder="e.g. John and Sarah"
                className="vf-input"
                data-testid="vf-seller-name"
              />
            </div>
            <div className="vf-field">
              <Label className="vf-label">Property address</Label>
              <Input
                value={propertyAddress}
                onChange={(e) => setPropertyAddress(e.target.value)}
                placeholder="e.g. 28 Beaucroft Lane, Wimborne"
                className="vf-input"
                data-testid="vf-property-address"
              />
            </div>
          </div>

          {/* Length cards */}
          <div className="vf-field">
            <Label className="vf-label">Script Length</Label>
            <div className="vf-length-toggle">
              <button
                className={`vf-length-btn ${scriptLength === "Short" ? "active" : ""}`}
                onClick={() => setScriptLength("Short")}
                data-testid="vf-length-short"
              >
                <FileText size={20} />
                <span>Covering Note</span>
                <span className="vf-length-hint">Brief intro + CTA</span>
              </button>
              <button
                className={`vf-length-btn ${scriptLength === "Medium" ? "active" : ""}`}
                onClick={() => setScriptLength("Medium")}
                data-testid="vf-length-medium"
              >
                <Sparkles size={20} />
                <span>Long Format</span>
                <span className="vf-length-hint">Full follow-up ~60–90s</span>
              </button>
            </div>
            {scriptLength === "Short" && (
              <div className="vf-info-note">
                A brief covering note — meet & greet, mention the valuation is
                enclosed, state the asking price, and your call-to-action.
                Market reasoning and personal notes are skipped here (use Long
                Format for the full personalised script).
              </div>
            )}
          </div>

          {/* Proposed price — required for both Covering Note and Long Format */}
          <div className="vf-field">
            <Label className="vf-label">
              <PoundSterling size={14} /> Proposed listing price *
            </Label>
            <Input
              value={proposedPrice}
              onChange={(e) => setProposedPrice(e.target.value)}
              placeholder="e.g. £675,000"
              className="vf-input"
              data-testid="vf-proposed-price"
            />
          </div>

          {scriptLength === "Medium" && (
            <>
              <div className="vf-row-2">
                <div className="vf-field">
                  <Label className="vf-label">
                    <Star size={14} /> Special feature (optional)
                  </Label>
                  <Input
                    value={specialFeature}
                    onChange={(e) => setSpecialFeature(e.target.value)}
                    placeholder="e.g. the south-facing garden"
                    className="vf-input"
                    data-testid="vf-special-feature"
                  />
                </div>
                <div className="vf-field">
                  {/* Spacer to balance the row layout */}
                </div>
              </div>

              <div className="vf-chip-row">
                {FEATURE_SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="vf-chip"
                    onClick={() => setSpecialFeature(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <div className="vf-field">
                <Label className="vf-label">
                  <Heart size={14} /> Personal touch (optional)
                </Label>
                <Input
                  value={personalTouch}
                  onChange={(e) => setPersonalTouch(e.target.value)}
                  placeholder="e.g. your dog Bertie was such a cheeky chap"
                  className="vf-input"
                  data-testid="vf-personal-touch"
                />
                <div className="vf-chip-row">
                  {PERSONAL_SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="vf-chip"
                      onClick={() => setPersonalTouch(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="vf-field">
                <Label className="vf-label">Comparable sales (optional)</Label>
                <Textarea
                  value={comparableSales}
                  onChange={(e) => setComparableSales(e.target.value)}
                  placeholder="e.g. Two recent sales on Northleigh Lane at £640k and £710k"
                  className="vf-input vf-textarea"
                  data-testid="vf-comparables"
                  rows={2}
                />
              </div>
            </>
          )}

          {/* CTA pills */}
          <div className="vf-field">
            <Label className="vf-label">
              <Send size={14} /> Call to action
            </Label>
            <div className="vf-cta-toggle">
              {CTA_PRESETS.map((cta) => {
                const Icon = cta.icon;
                const active = selectedCta === cta.label;
                return (
                  <button
                    key={cta.id}
                    type="button"
                    className={`vf-cta-btn ${active ? "active" : ""}`}
                    onClick={() => setSelectedCta(cta.label)}
                    data-testid={`vf-cta-${cta.id}`}
                  >
                    <Icon size={14} />
                    <span>{cta.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <Button
            className="vf-generate-btn"
            onClick={handleGenerate}
            disabled={
              generating ||
              (scriptLength === "Medium" && !proposedPrice.trim()) ||
              (scriptLength === "Short" && !sellerName.trim())
            }
            data-testid="vf-generate-btn"
          >
            {generating ? (
              <>
                <Loader2 size={18} className="vf-spin" /> Generating…
              </>
            ) : (
              <>
                <Sparkles size={18} /> Generate Follow-Up Script
              </>
            )}
          </Button>
        </div>

        {/* Output Column */}
        <div className="vf-output-col">
          {generating && (
            <div className="vf-generating">
              <Loader2 size={32} className="vf-spin" />
              <h3>Generating your follow-up</h3>
              <p>Drafting the script in your agency style…</p>
            </div>
          )}

          {!generating && !generatedScript && (
            <div className="vf-empty-output">
              <Home size={32} />
              <h3>Your follow-up will appear here</h3>
              <p>Fill in the details on the left and hit Generate</p>
            </div>
          )}

          {!generating && generatedScript && (
            <div className="vf-result">
              <div className="vf-result-block">
                <div className="vf-result-header">
                  <h3>
                    <FileText size={16} />
                    {scriptLength === "Short" ? "Covering Note" : "Long Format Follow-Up"}
                  </h3>
                  <button
                    type="button"
                    className="vf-copy-btn"
                    onClick={handleCopy}
                    data-testid="vf-copy-btn"
                  >
                    {copied ? <ClipboardCheck size={14} /> : <Copy size={14} />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <div className="vf-script-text" data-testid="vf-generated-script">
                  {generatedScript}
                </div>
              </div>

              <Button
                className="vf-submit-btn"
                onClick={() => setSubmitOpen(true)}
                data-testid="vf-submit-btn"
              >
                <Send size={16} /> Submit to Editors
              </Button>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit to Editors?</AlertDialogTitle>
            <AlertDialogDescription>
              This will send the valuation follow-up script to the editing team
              for production with Synthesia / your usual workflow.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Submitting…" : "Submit"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
